import React, { useMemo } from "react";
import Pagination from "react-bootstrap/Pagination";

const range = (start, end) =>
  Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);

/**
 * Build page numbers with ellipsis for large page counts (e.g. 1 … 4 5 6 … 20).
 */
const buildPageList = (current, total, siblingCount = 1) => {
  if (total <= 0) return [];
  if (total <= 7) return range(1, total);

  const items = new Set([1, total]);
  for (let i = current - siblingCount; i <= current + siblingCount; i++) {
    if (i >= 1 && i <= total) items.add(i);
  }

  const sorted = [...items].sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) out.push("ellipsis");
    out.push(p);
    prev = p;
  }
  return out;
};

const PaginationComponent = ({
  total = 0,
  itemsPerPage = 10,
  currentPage = 1,
  onPageChange,
  size,
  showFirstLast = true,
  showInfo = false,
  siblingCount = 1,
  className = "",
}) => {
  const totalPages = useMemo(() => {
    if (!itemsPerPage || itemsPerPage < 1) return 0;
    return Math.ceil(Math.max(0, total) / itemsPerPage);
  }, [total, itemsPerPage]);

  const pageList = useMemo(
    () => buildPageList(currentPage, totalPages, siblingCount),
    [currentPage, totalPages, siblingCount]
  );

  const goTo = (page) => {
    if (typeof onPageChange !== "function") return;
    const p = Number(page);
    if (!Number.isFinite(p)) return;
    const clamped = Math.min(Math.max(1, Math.round(p)), totalPages);
    onPageChange(clamped);
  };

  if (totalPages === 0) return null;

  const navLabel = `Pagination navigation, page ${currentPage} of ${totalPages}`;

  const controls = (
    <Pagination size={size} className={`flex-wrap mb-0 ${className}`.trim()}>
      {showFirstLast && (
        <Pagination.First
          aria-label="First page"
          disabled={currentPage <= 1}
          onClick={() => goTo(1)}
        />
      )}
      <Pagination.Prev
        aria-label="Previous page"
        disabled={currentPage <= 1}
        onClick={() => goTo(currentPage - 1)}
      />
      {pageList.map((item, idx) =>
        item === "ellipsis" ? (
          <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
        ) : (
          <Pagination.Item
            key={item}
            active={item === currentPage}
            aria-label={item === currentPage ? `Current page, page ${item}` : `Go to page ${item}`}
            aria-current={item === currentPage ? "page" : undefined}
            onClick={() => goTo(item)}
          >
            {item}
          </Pagination.Item>
        )
      )}
      <Pagination.Next
        aria-label="Next page"
        disabled={currentPage >= totalPages}
        onClick={() => goTo(currentPage + 1)}
      />
      {showFirstLast && (
        <Pagination.Last
          aria-label="Last page"
          disabled={currentPage >= totalPages}
          onClick={() => goTo(totalPages)}
        />
      )}
    </Pagination>
  );

  if (!showInfo) {
    return (
      <nav aria-label={navLabel} className="d-inline-flex">
        {controls}
      </nav>
    );
  }

  return (
    <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2">
      <span className="text-muted small">
        Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
        <span className="ms-1">· {total} total</span>
      </span>
      <nav aria-label={navLabel}>{controls}</nav>
    </div>
  );
};

export default PaginationComponent;
